import type React from 'react';
import { useMemo } from 'react';
import { isNil } from 'ramda';
import { useIntl } from '@/components/formatted_message/FormattedMessage';
import { SUPPLY_POINT_CATEGORY_TRANSLATED as message } from '@/common/messages/supply_point/category';
import { COMMODITY_TRANSLATED } from '@/common/messages/entities';
import { Commodity } from '@enmon/commons';
import { TreeSelect, type TreeSelectProps } from './TreeSelect';
import { getCategoriesByCommodity, type SupplyPointCategoryType } from '@enmon/commons/lib/enmon';

const { SHOW_CHILD } = TreeSelect;

export interface SupplyPointCategoriesTreeSelectProps
  extends Pick<TreeSelectProps, 'allowClear' | 'disabled' | 'id' | 'placeholder'> {
  commodities?: Commodity[] | undefined;
  value?: SupplyPointCategoryType[];
  categoriesToOmit?: SupplyPointCategoryType[] | undefined;
  onChange?: (value: SupplyPointCategoryType[] | undefined) => void;
}

export const SupplyPointCategoriesTreeSelect: React.FC<SupplyPointCategoriesTreeSelectProps> = ({
  commodities,
  categoriesToOmit,
  value,
  onChange,
  ...restProps
}) => {
  const intl = useIntl();

  const treeData = useMemo<TreeSelectProps['treeData']>(() => {
    const toLeaf = (category: SupplyPointCategoryType) => {
      const messageDescriptor = message[category];

      return {
        value: category,
        label: isNil(messageDescriptor) ? category : intl.formatMessage(messageDescriptor),
      };
    };

    const selectedCommodities = commodities?.length ? commodities : Object.values(Commodity);

    return selectedCommodities.map(groupCommodity => {
      const categories = getCategoriesByCommodity(groupCommodity).filter(category =>
        categoriesToOmit?.length ? !categoriesToOmit?.includes(category) : true,
      );

      return {
        value: groupCommodity,
        label: intl.formatMessage(COMMODITY_TRANSLATED[groupCommodity]),
        children: categories.map(toLeaf),
      };
    });
  }, [commodities, intl, categoriesToOmit]);

  return (
    <TreeSelect
      treeCheckable
      showSearch
      maxTagCount="responsive"
      popupMatchSelectWidth={false}
      showCheckedStrategy={SHOW_CHILD}
      treeData={treeData}
      treeDefaultExpandAll
      treeNodeFilterProp="label"
      value={value}
      onChange={value => onChange?.(value?.length ? value : undefined)}
      {...restProps}
    />
  );
};
